import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { createCampaignGeneralSettingSchema } from '../../assets/schemas/schemas';
import { notify } from '../../assets/helpers/toast';
import { useForm } from 'react-hook-form';
import { activationDaysConfigService } from '../../services/activationDaysConfigService';

const formatDateForApi = (date) => {
  const sourceDate = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(sourceDate.getTime())) {
    return new Date().toISOString().split('T')[0];
  }

  return sourceDate.toISOString().split('T')[0];
};

export default function useCampaignGeneralSettings() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    getValues,
    control,
  } = useForm({
    resolver: yupResolver(createCampaignGeneralSettingSchema()),
    defaultValues: {
      activationDays: 0,
      beginDate: new Date(),
    },
  });

  const [currentConfig, setCurrentConfig] = useState(null);
  const [historyData, setHistoryData] = useState({
    limit: 10,
    offset: 0,
    data: [],
    total: 0,
    isLoading: false,
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleHistoryPageChange = useCallback((_e, page) => {
    setHistoryData((prev) => ({
      ...prev,
      offset: (page - 1) * prev.limit,
    }));
  }, []);

  const onHistoryLimitChange = useCallback((event) => {
    const value = Number(event.target.value);

    setHistoryData((prev) => ({
      ...prev,
      limit: value,
      offset: 0,
    }));
  }, []);

  const fetchConfigByDate = useCallback(
    async (date = new Date(), abortSignal) => {
      const queryDate = formatDateForApi(date);
      const response = await activationDaysConfigService.getConfigByDate(queryDate);

      if (abortSignal?.aborted) return;

      if ([200, 201].includes(response?.status)) {
        const config = response?.data?.config;
        if (config) {
          setCurrentConfig(config);
          console.log('@ Config encontrada:', config);
          setValue('activationDays', Number(config.activationDays ?? 0), {
            shouldValidate: true,
          });
          setValue(
            'beginDate',
            config.beginDate ? new Date(config.beginDate) : new Date(queryDate),
            {
              shouldValidate: true,
            }
          );
        } else {
          setCurrentConfig(null);
          setValue('activationDays', 0, { shouldValidate: true });
          setValue('beginDate', new Date(queryDate), { shouldValidate: true });
        }
      } else if (response?.status === 404) {
        setCurrentConfig(null);
        setValue('activationDays', 0, { shouldValidate: true });
        setValue('beginDate', new Date(queryDate), { shouldValidate: true });
      } else {
        notify(
          response?.message ??
            response?.data?.message ??
            'Error al obtener la configuración de activación',
          'error'
        );
      }
    },
    [setValue]
  );

  const fetchHistory = useCallback(
    async (abortSignal) => {
      setHistoryData((prev) => ({ ...prev, isLoading: true }));

      try {
        const response = await activationDaysConfigService.getHistory({
          limit: historyData.limit,
          offset: historyData.offset,
        });

        if (abortSignal?.aborted) return;

        if ([200, 201].includes(response?.status)) {
          const rows = response?.data?.history || [];

          setHistoryData((prev) => ({
            ...prev,
            data: rows,
            total: response?.data?.total ?? rows.length,
            isLoading: false,
          }));
        } else {
          notify(
            response?.message ??
              response?.data?.message ??
              'Error al cargar el historial de configuración',
            'error'
          );
          setHistoryData((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      } catch (error) {
        if (abortSignal?.aborted) return;
        notify('Error al cargar el historial de configuración', 'error');
        setHistoryData((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [historyData.limit, historyData.offset]
  );

  const onSubmit = handleSubmit(async (data) => {
    setIsSaving(true);

    const payload = {
      activationDays: Number(data.activationDays),
      beginDate: formatDateForApi(data.beginDate),
    };

    const response = await activationDaysConfigService.createConfig(payload);
    setIsSaving(false);

    if ([200, 201].includes(response?.status)) {
      notify('Configuración creada', 'success');
      await fetchHistory();
      resetToToday();
    } else {
      notify(
        response?.message ??
          response?.data?.message ??
          'Error al crear la configuración',
        'error'
      );
    }
  });

  const resetToToday = useCallback(() => {
    const today = new Date();
    reset({
      activationDays: 0,
      beginDate: today,
    });
  }, [reset]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchConfigByDate(new Date(), abortController.signal);
    fetchHistory(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchConfigByDate, fetchHistory]);

  return {
    register,
    errors,
    currentConfig,
    historyData,
    isSaving,
    handleHistoryPageChange,
    onHistoryLimitChange,
    fetchConfigByDate,
    fetchHistory,
    onSubmit,
    resetToToday,
    setValue,
    getValues,
    control,
  };
}
